import Popover from "./popover"

const LOCATION_VALUE_TARGETS = {
  county: "countySelect",
  region: "regionSelect",
  program_location: "programLocationSelect"
}

// Manages the floating filter popover on the Program Events admin index:
//
//   - Popover open/close (inherited from Popover)
//   - Radio-driven input swaps
//   - Location mode toggle
//   - Reset / Apply enable-disable, plus Reset's hybrid behavior
export default class extends Popover {
  static targets = [
    "popover",
    "toggle",
    "nameOrIdInput",
    "nameOrIdLabel",
    "instructorIdInput",
    "dateFromInput",
    "dateToInput",
    "statusSelect",
    "visibilitySelect",
    "zipGroup",
    "countyGroup",
    "regionGroup",
    "programLocationGroup",
    "zipInput",
    "radiusSelect",
    "countySelect",
    "regionSelect",
    "programLocationSelect",
    "applyButton",
    "resetButton",
    "windowStart",
    "windowEnd"
  ]

  static values = {
    filtersApplied: Boolean,
    resetUrl: String,
    defaultRadius: String,
    // { name: {name,label,placeholder}, id: {...} } — localized server-side and passed in
    // so the radio swap doesn't hardcode user-facing copy in JS.
    nameOrIdSpecs: Object
  }

  connect() {
    this.syncDateBounds()
    this.updateActionsState()
  }

  // Native date fields: mirror each end's value onto the other's bound so the end can't precede
  // the start. An empty value clears the bound, so an open-ended range (only one date set) stays
  // unconstrained. Values are ISO (yyyy-mm-dd), which is what the min/max attributes expect.
  syncDateBounds() {
    if (!this.hasDateFromInputTarget || !this.hasDateToInputTarget) return

    this.dateToInputTarget.min = this.dateFromInputTarget.value
    this.dateFromInputTarget.max = this.dateToInputTarget.value
  }

  // A date field changed: re-sync the range bounds, then re-evaluate Apply/Reset.
  dateChanged() {
    this.syncDateBounds()
    this.updateActionsState()
  }

  // The filter toggle button owns aria-expanded; guard hasToggleTarget so the base's bookkeeping
  // no-ops if the popover is ever rendered without one.
  get ariaExpander() {
    return this.hasToggleTarget ? this.toggleTarget : null
  }

  // ----- Radio-driven input swaps -----

  swapNameOrId(event) {
    this.applyNameOrId(event.target.value)
  }

  applyNameOrId(value) {
    this.swapRadioField(
      value,
      this.nameOrIdSpecsValue,
      this.nameOrIdInputTarget,
      this.nameOrIdLabelTarget
    )
  }

  swapRadioField(radioValue, valueToSpec, input, label) {
    const spec = valueToSpec[radioValue]
    if (!spec) return
    input.name = spec.name
    input.placeholder = spec.placeholder
    label.textContent = spec.label
    input.value = ""
    this.updateActionsState()
  }

  // ----- Location mode toggle -----

  switchLocation(event) {
    this.applyLocationMode(event.target.value)
  }

  // Shows the active sub-group and disables the others. Inactive groups keep their value but,
  // being disabled, aren't serialized on submit — so toggling modes back and forth is
  // non-destructive, while a submit under a different mode still drops the unsent value (and the
  // server re-renders without that chip). Only an explicit Reset clears the values.
  applyLocationMode(mode) {
    const groups = {
      zip: this.zipGroupTarget,
      county: this.countyGroupTarget,
      region: this.regionGroupTarget,
      program_location: this.programLocationGroupTarget
    }
    Object.entries(groups).forEach(([key, group]) => {
      const active = key === mode
      this.toggleHidden(group, !active)
      this.setGroupDisabled(group, !active)
    })
    this.updateActionsState()
  }

  resetSelect(el) {
    const isRadius = this.hasRadiusSelectTarget && el === this.radiusSelectTarget
    el.value = isRadius ? this.defaultRadiusValue : ""
  }

  toggleHidden(el, hidden) {
    if (hidden) {
      el.setAttribute("hidden", "hidden")
    } else {
      el.removeAttribute("hidden")
    }
  }

  setGroupDisabled(group, disabled) {
    group.querySelectorAll("input, select").forEach((el) => { el.disabled = disabled })
  }

  // ----- Reset -----

  // Hybrid Reset. When the page was loaded WITH filters applied, the results are server-rendered from
  // the URL, so clearing them means navigating to the unfiltered index — done by pointing the
  // `program-events-main` turbo-frame at the reset URL (swaps in place + advances the URL, no full
  // reload). When no filters are applied (results already unfiltered), there's nothing to fetch, so
  // just wipe the form in place.
  reset(event) {
    event.preventDefault()

    if (this.filtersAppliedValue) {
      document.getElementById("program-events-main").setAttribute("src", this.resetUrl())
      return
    }

    this.clearForm()
  }

  // The reset target, carrying the calendar's current month (calendar mode only — the hidden window
  // fields the calendar controller keeps in sync) so clearing filters keeps the viewed month rather
  // than snapping back to the initial one.
  resetUrl() {
    const url = new URL(this.resetUrlValue, window.location.origin)

    if (this.hasWindowStartTarget && this.windowStartTarget.value && this.windowEndTarget.value) {
      url.searchParams.set("mode", "calendar")
      url.searchParams.set("window_start", this.windowStartTarget.value)
      url.searchParams.set("window_end", this.windowEndTarget.value)
    }

    return url.pathname + url.search
  }

  clearForm() {
    // Query by element type (not styling class) so a CSS rename can't silently break
    // form-clearing. Radios are excluded by the type filter and reset via checkRadio below.
    this.element.querySelectorAll("input[type='text'], input[type='date']").forEach((el) => { el.value = "" })
    this.element.querySelectorAll("select").forEach((el) => this.resetSelect(el))
    // Dates were just cleared — drop the range min/max bounds so neither field stays constrained.
    this.syncDateBounds()
    // The instructor id lives in a hidden field driven by the React typeahead, which owns
    // both the submitted id and the visible text. The text/date sweep above misses the
    // hidden field, and clearing its value wouldn't reset the React-rendered display — so
    // clear the value AND signal the component (via `autocomplete:clear`) to reset itself.
    if (this.hasInstructorIdInputTarget) {
      this.instructorIdInputTarget.value = ""
      this.instructorIdInputTarget.dispatchEvent(new CustomEvent("autocomplete:clear"))
    }

    this.checkRadio("search_name_or_id", "name")
    this.applyNameOrId("name")
    this.checkRadio("search_location_kind", "zip")
    this.applyLocationMode("zip")

    this.updateActionsState()
  }

  checkRadio(name, value) {
    const radio = this.element.querySelector(`input[name='${name}'][value='${value}']`)
    if (radio) radio.checked = true
  }

  // ----- Submit -----

  // Bound via `submit->` on the search form. The form submits natively as a GET, so EVERY field
  // serializes into the address bar (the frame `advance`s that URL). Disable the noise right before
  // the GET so only the active filters ride along — the backend already ignores all of it, this just
  // keeps the URL honest. Disabling is momentary: the frame swap re-renders a fresh, enabled form.
  pruneOnSubmit(event) {
    if (this.hasZipInputTarget && this.hasRadiusSelectTarget && !this.zipInputTarget.value.trim()) {
      this.radiusSelectTarget.disabled = true
    }

    const form = event.target

    form.querySelectorAll("input[name='search_name_or_id'], input[name='search_location_kind']")
      .forEach((radio) => { radio.disabled = true })

    form.querySelectorAll("input[type='text'], input[type='date'], input[type='hidden'], select")
      .forEach((field) => { if (!field.value.trim()) field.disabled = true })
  }

  // ----- Apply / Reset enable-disable -----

  updateActionsState() {
    const hasValue = this.formHasValue()
    if (this.hasApplyButtonTarget) this.applyButtonTarget.disabled = !hasValue
    if (this.hasResetButtonTarget) this.resetButtonTarget.disabled = !(this.filtersAppliedValue || hasValue)
  }

  formHasValue() {
    const values = [
      this.targetValue("nameOrIdInput"),
      this.targetValue("instructorIdInput"),
      this.targetValue("dateFromInput"),
      this.targetValue("dateToInput"),
      this.targetValue("statusSelect"),
      this.targetValue("visibilitySelect")
    ]
    // Location counts as a filter by its active mode's value only. In zip mode that's the
    // zip string — radius is never a filter on its own (a radius without a zip means
    // nothing, and a radius WITH a zip is already covered by the zip), so its value is
    // intentionally ignored here.
    if (this.currentLocationMode() === "zip") {
      values.push(this.targetValue("zipInput"))
    } else {
      values.push(this.targetValue(LOCATION_VALUE_TARGETS[this.currentLocationMode()]))
    }
    return values.some((v) => v != null && v !== "")
  }

  // The checked location-kind radio's value (zip/county/region/program_location), or null.
  currentLocationMode() {
    const checked = this.element.querySelector("input[name='search_location_kind']:checked")
    return checked ? checked.value : null
  }

  targetValue(name) {
    if (!name) return null

    const hasTarget = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`
    if (!this[hasTarget]) return null
    return this[`${name}Target`].value
  }
}
